import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { logEventDebounced, computePromptDiff } from "../utils/logger";
import {
  FaSyncAlt,
  FaEdit,
  FaSave,
  FaRobot,
  FaProjectDiagram,
  FaRoute,
  FaFileAlt,
  FaExclamationTriangle,
  FaComments
} from "react-icons/fa";
import { getApiBase } from "../apiBase";



const PROMPT_NAMES = {
  qrt: { label: "Query Rewriter Prompt", icon: <FaRobot /> },
  dpt: { label: "Decomposition Prompt", icon: <FaProjectDiagram /> },
  rpt: { label: "Routing Prompt", icon: <FaRoute /> },
  spt: { label: "Summarizer Prompt", icon: <FaFileAlt /> },
  sfpt: { label: "Summarizer FallBack Prompt", icon: <FaExclamationTriangle /> },
  cpt: { label: "Conversational Prompt", icon: <FaComments /> }
};

export default function PromptAfterOnboarding() {
  const { tenantId, botId } = useParams();
  const resolvedTenantId = tenantId || localStorage.getItem("tenant_id");
  const resolvedBotId = botId || localStorage.getItem("bot_id");

  const [prompts, setPrompts] = useState({});
  const [selectedKey, setSelectedKey] = useState("qrt");
  const [editMode, setEditMode] = useState(false);
  const [value, setValue] = useState("");
  const [toast, setToast] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [botName, setBotName] = useState("Your Bot");
  const [showConfirm, setShowConfirm] = useState(false);


  

  const showToast = (text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2500);
  };

  const fetchPrompts = async () => {
  logEventDebounced("PROMPTS_FETCH_START", { tenant: resolvedTenantId, bot: resolvedBotId });

  const res = await fetch(
    `${getApiBase()}/api/get-prompts?tenantid=${resolvedTenantId}&botid=${resolvedBotId}`
  );
  const data = await res.json();

  const activePrompts = {};

Object.keys(data).forEach((key) => {
  const item = data[key];

let active;

if (Array.isArray(item)) {
  active = item.find(p => p.is_active) || item[0];
} else {
  active = {
    prompt_template: item,
    version_number: 1
  };
}

activePrompts[key] = {
  value: active?.prompt_template || "",
  version: active?.version_number || 1
};
});

setPrompts(activePrompts);
setValue(activePrompts[selectedKey]?.value || "");

  logEventDebounced("PROMPTS_FETCH_SUCCESS", { total: Object.keys(data).length });
};

const fetchBotName = async () => {
  if (!resolvedTenantId || !resolvedBotId) return;

  try {
    const res = await fetch(
      `${getApiBase()}/tenant/${resolvedTenantId}/bot/${resolvedBotId}/details`
    );

    if (!res.ok) throw new Error("Failed to fetch bot details");

    const json = await res.json();

    setBotName(json?.data?.bot_name || "Your Bot"); 
  } catch (err) {
    console.error("Bot name fetch failed:", err);
    setBotName("Your Bot");
  }
};





  useEffect(() => {
  if (!resolvedTenantId || !resolvedBotId) return;

  fetchPrompts();
  fetchBotName();
}, [resolvedTenantId, resolvedBotId]);

  useEffect(() => {
    if (prompts[selectedKey]?.value) setValue(prompts[selectedKey]?.value);
  }, [selectedKey, prompts]);

  const handleSave = async () => {
  if (!editMode) {
    logEventDebounced("PROMPT_EDIT_START", { prompt: selectedKey });
    return setEditMode(true);
  }

  const oldValue = prompts[selectedKey]?.value || "";
const diff = computePromptDiff(oldValue, value);

logEventDebounced("PROMPT_SAVE_SUCCESS", {
  prompt: selectedKey,
  totalLinesChanged: diff.totalLinesChanged,
  changes: JSON.stringify(diff.changes)});


  const res = await fetch(`${getApiBase()}/api/update-prompt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tenantid: resolvedTenantId,
      botid: resolvedBotId,
      column: selectedKey,
      value
    })
  });

  if (res.ok) {
    setPrompts((prev) => ({ ...prev, [selectedKey]: value }));
    setEditMode(false);
    showToast("Saved successfully");

    logEventDebounced("PROMPT_SAVE_SUCCESS", {
      prompt: selectedKey,
      oldChars: diff.oldChars,
      newChars: diff.newChars,
      oldPreview: diff.oldPreview,
      newPreview: diff.newPreview
    });

  } else {
    showToast("Save failed");
    logEventDebounced("PROMPT_SAVE_ERROR", { prompt: selectedKey });
  }
};

  const handleGenerate = async () => {
  setShowConfirm(false);

  logEventDebounced("PROMPTS_GENERATE_START", { bot: resolvedBotId });

  try {
    setGenerating(true);

    await fetch(
      `${getApiBase()}/tenant/${resolvedTenantId}/bot/${resolvedBotId}/generate-prompts`,
      { method: "POST" }
    );

    await fetchPrompts();
    showToast("Prompts generated!");

    logEventDebounced("PROMPTS_GENERATE_SUCCESS", { bot: resolvedBotId });
  } finally {
    setGenerating(false);
  }
};


  return (
    <div className="flex h-screen w-full bg-[#F8F4F2] overflow-hidden">
      {toast && (
        <div className="fixed bottom-5 right-5 bg-[#823B55] text-white px-5 py-3 rounded-xl shadow-xl z-50">
          {toast}
        </div>
      )}

      <div className="flex flex-col bg-white flex-1 min-w-0">
        <div className="flex justify-between items-center px-6 py-4 border-b bg-white">
          <div>
  <h1 className="text-xl font-bold text-[#5E3C58] tracking-tight">
    Agent Script
    <span className="text-gray-400 font-medium mx-2">–</span>
    <span className="text-[#2F1B2C]">{botName}</span>
  </h1>

  <div className="flex items-center gap-2 mt-1 text-sm text-[#823B55]">
  {PROMPT_NAMES[selectedKey].icon}
  <span className="font-medium flex items-center gap-2">
    {PROMPT_NAMES[selectedKey].label}
    <span className="ml-2 px-2 py-[2px] text-[10px] italic bg-[#F3E8EF] text-[#823B55] rounded-full">
      version: v{prompts[selectedKey]?.version || 1}
    </span>
  </span>
</div>
          </div>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 h-9 px-5 bg-[#823B55] text-white text-sm font-medium rounded-lg hover:bg-[#6E4466] transition"
          >
            {editMode ? <FaSave /> : <FaEdit />}
            {editMode ? "Save" : "Edit"}
          </button>
        </div>

        <div className="relative flex-1 min-h-0 min-w-0 w-full bg-white overflow-hidden">
          <pre
  className={`absolute inset-0 w-full h-full px-6 py-5
  overflow-y-scroll
  text-sm
  font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation Mono','Courier New',monospace]
  leading-[1.65]
  text-gray-700
  whitespace-pre-wrap
  bg-white box-border
  ${!editMode ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
>
  {value}
</pre>


          <textarea
  value={value}
  onChange={(e) => setValue(e.target.value)}
  className={`absolute inset-0 w-full h-full px-6 py-5
  resize-none outline-none
  overflow-y-scroll
  text-sm
  font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation Mono','Courier New',monospace]
  leading-[1.65]
  bg-white text-gray-800 box-border
  ${editMode ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
  placeholder="Edit your system prompt here..."
/>

        </div>
      </div>

      <div className="w-[260px] min-w-[260px] bg-white border-l flex flex-col">
        <div className="px-6 py-5 border-b">
          <h2 className="text-lg font-bold text-[#823B55] flex items-center gap-2">
            Agent Script
          </h2>
        </div>

        <div className="px-5 py-3">
          <button
            onClick={() => setShowConfirm(true)}
            disabled={generating}
            className="w-full flex justify-center items-center gap-2 h-9 text-sm rounded-lg font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition"
          >
            <FaSyncAlt className={generating ? "animate-spin" : ""} />
            {generating ? "Generating..." : "Auto Generate"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {Object.keys(PROMPT_NAMES).map((key) => (
            <button
              key={key}
              onClick={() => {
                setSelectedKey(key);
                setEditMode(false);
                logEventDebounced("PROMPT_TAB_SWITCH", { selected: key });
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-sm transition-all
                ${selectedKey === key
                  ? "bg-[#F5E9F1] text-[#823B55] font-semibold shadow-sm"
                  : "text-gray-700 hover:bg-gray-100"}
              `}
            >
              <span className="text-[#823B55]">
                {PROMPT_NAMES[key].icon}
              </span>
              {PROMPT_NAMES[key].label}
            </button>
          ))}
        </div>

        {showConfirm && (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-6 w-[420px] shadow-2xl border border-[#F1E4EC]">

      <div className="flex items-start gap-3 mb-4">
        <div className="bg-[#FDF2E9] text-[#E6A23C] p-2 rounded-lg">
          <FaExclamationTriangle />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-[#2F1B2C]">
            Replace existing scripts?
          </h3>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            This will overwrite your current prompts with AI-generated content. This action cannot be undone.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => setShowConfirm(false)}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
        >
          Cancel
        </button>

        <button
          onClick={handleGenerate}
          className="px-4 py-2 text-sm rounded-lg bg-[#823B55] text-white hover:bg-[#6E4466] transition"
        >
          Continue
        </button>
      </div>

    </div>
  </div>
)}
      </div>
    </div>
  );
}